import { useEffect, useState } from "react"

import { useParams } from "react-router-dom"

import { CollectionItem, ContentObjectItem } from "@vertesia/common"
import { FolderPlus, FolderMinus, FolderSearch } from "lucide-react"
import { toast } from "sonner"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@ui/dialog"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@ui/select"

interface CollectionButtonProps {
    document: ContentObjectItem
    onActionDone?: () => void
}
export function CollectionButton({ document, onActionDone }: Readonly<CollectionButtonProps>) {
    const { cId } = useParams()

    const [addToCollection, setAddToCollection] = useState(cId ? false : true)
    const [createCollection, setCreateCollection] = useState(false)
    const [collectionName, setCollectionName] = useState<string | undefined>(undefined)

    const [collections, setCollections] = useState<CollectionItem[]>([])
    const [filterValue, setFilterValue] = useState("")

    const filteredOptions = collections?.filter((collection) => collection.name.toLowerCase().includes(filterValue.toLowerCase()))

    const { client } = useUserSession()

    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [collectionId, setCollectionId] = useState<string | undefined>(undefined)

    const handleOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        setCollectionId(undefined)
        setCollectionName(undefined)
        setCreateCollection(false)
        setFilterValue("")
        onActionDone?.()
    }

    const handleAction = () => {
        if (addToCollection) {
            handleAddToCollection()
        } else {
            handleRemoveFromCollection()
        }
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
    }

    const handleAddToCollection = async () => {
        if (!client || !addToCollection || (createCollection && !collectionName) || (!createCollection && !collectionId)) {
            return
        }

        if (createCollection && collectionName) {
            setIsLoading(true)
            client.store.collections
                .create({ name: collectionName, dynamic: false })
                .then((collection) => {
                    client.store.collections
                        .addMembers(collection.id, [document.id])
                        .then(() => {
                            toast.success("Document added to Collection", {
                                description: "Document successfully added to Collection",
                                duration: 2000,
                            })
                            handleClose()
                        })
                        .catch((error) => {
                            toast.error("Error adding Document to Collection", {
                                description: error.message,
                                duration: 2000,
                            })
                        })
                        .finally(() => {
                            setIsLoading(false)
                        })
                })
                .catch((error) => {
                    toast.error("Error creating Collection", {
                        description: error.message,
                        duration: 2000,
                    })
                    setIsLoading(false)
                })
        } else if (!createCollection && collectionId) {
            setIsLoading(true)
            client.store.collections
                .addMembers(collectionId, [document.id])
                .then(() => {
                    toast.success("Document added to Collection", {
                        description: "Document successfully added to Collection",
                        duration: 2000,
                    })
                    handleClose()
                })
                .catch((error) => {
                    toast.error("Error adding Document to Collection", {
                        description: error.message,
                        duration: 2000,
                    })
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }
    }

    const handleRemoveFromCollection = async () => {
        if (!client || addToCollection || !cId) {
            return
        }

        setIsLoading(true)
        client.store.collections
            .deleteMembers(cId, [document.id])
            .then(() => {
                toast.success("Document removed from Collection", {
                    description: "Document successfully removed from Collection",
                    duration: 2000,
                })
                handleClose()
                setAddToCollection(true)
            })
            .catch((error) => {
                toast.error("Error removing Document from Collection", {
                    description: error.message,
                    duration: 2000,
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleCreateCollection = () => {
        setCreateCollection(true)
        setCollectionName(filterValue)
        setFilterValue("")
    }

    const searchCollections = async () => {
        client.store.collections.search({ dynamic: false }).then((response) => {
            setCollections(response)
        })
    }

    useEffect(() => {
        if (!client || isLoading || !addToCollection) {
            return
        }

        searchCollections()
    }, [client, isLoading])

    return (
        <>
            <Button onClick={handleOpen} variant="ghost" className="text-left justify-start w-full">
                {addToCollection ? <FolderPlus /> : <FolderMinus />}
                <span>{addToCollection ? "Add to Collection" : "Remove from Collection"}</span>
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{addToCollection ? "Add to Collection" : "Remove from Collection"}</DialogTitle>
                    </DialogHeader>
                    {addToCollection ? (
                        <>
                            {createCollection ? (
                                <div className="flex flex-col gap-0.5">
                                    <Label>Create a Collection</Label>
                                    <div className="flex flex-row w-full gap-2">
                                        <Input placeholder="Collection Name" value={collectionName} onChange={(e) => setCollectionName(e.target.value)} className="w-full" />
                                        <Button
                                            onClick={() => {
                                                setCollectionName(undefined)
                                                setCreateCollection(false)
                                            }}
                                        >
                                            <FolderSearch />
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-0.5">
                                    <Label>Select a Collection</Label>
                                    <div className="flex flex-row w-full gap-2">
                                        <Select onValueChange={setCollectionId}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a Collection" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[250px]">
                                                <div className="flex flex-row items-center gap-2 p-2">
                                                    <Input placeholder="Search Collection(s)..." value={filterValue} onChange={(e) => setFilterValue(e.target.value)} onKeyDown={handleInputKeyDown} onClick={(e) => e.stopPropagation()} />
                                                </div>
                                                <SelectGroup>
                                                    {filteredOptions?.map((collection: any) => (
                                                        <SelectItem key={collection.id} value={collection.id} className="hover:cursor-pointer">
                                                            {collection.name}
                                                        </SelectItem>
                                                    ))}
                                                    {filteredOptions?.length === 0 && (
                                                        <div className="flex flex-col items-center gap-0.5 p-2">
                                                            <span className="text-gray-500">No collections found</span>
                                                            <Button className="w-full" onClick={handleCreateCollection}>
                                                                Create Collection
                                                            </Button>
                                                        </div>
                                                    )}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                        <Button
                                            onClick={() => {
                                                setCollectionName(undefined)
                                                setCreateCollection(true)
                                            }}
                                        >
                                            <FolderPlus />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">Are you sure you want to remove this document from the collection?</p>
                    )}

                    <DialogFooter>
                        <Button variant="destructive" onClick={handleClose} disabled={isLoading} className="hover:cursor-pointer">
                            Cancel
                        </Button>
                        <Button variant="outline" onClick={handleAction} disabled={isLoading || (addToCollection && !createCollection && !collectionId) || (addToCollection && createCollection && !collectionName)} className="hover:cursor-pointer">
                            {addToCollection ? (createCollection ? (isLoading ? "Creating..." : "Create Collection") : isLoading ? "Adding..." : "Add to Collection") : isLoading ? "Removing..." : "Remove"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
